import { useState } from "react";
import { ArrowUpRight, Heart, MessageSquare, Play } from "lucide-react";
import { SOCIAL_POSTS } from "@/data/turboData";
import { SOCIAL_FEED } from "@/constants/testIds";
import { PlatformIcon, platformColor } from "./PlatformIcon";

const PLATFORMS = ["all", "instagram", "facebook", "linkedin", "x", "youtube", "whatsapp"];

const SocialFeed = () => {
  const [filter, setFilter] = useState("all");
  const filtered = filter === "all" ? SOCIAL_POSTS : SOCIAL_POSTS.filter((p) => p.platform === filter);

  return (
    <section className="relative py-20 md:py-28 bg-[var(--tt-bg-soft)] border-y border-[var(--tt-line)]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--tt-accent)] font-semibold">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--tt-accent)] opacity-75 pulse-dot" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--tt-accent)]" />
              </span>
              Live · Updated weekly
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-[var(--tt-navy)] mt-3 leading-[1.05] max-w-2xl">
              Latest from our socials.
            </h2>
            <p className="mt-3 text-[var(--tt-ink-soft)] max-w-lg">
              The freshest posts, campaigns and updates we&apos;re running across
              Facebook, Instagram, LinkedIn, X, YouTube and WhatsApp.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 no-scrollbar overflow-x-auto">
            {PLATFORMS.map((p) => (
              <button
                key={p}
                data-testid={SOCIAL_FEED.platformFilter(p)}
                onClick={() => setFilter(p)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold capitalize transition border ${
                  filter === p
                    ? "bg-[var(--tt-navy)] text-white border-[var(--tt-navy)]"
                    : "bg-white text-[var(--tt-ink-soft)] border-[var(--tt-line)] hover:border-[var(--tt-navy)]"
                }`}
              >
                {p !== "all" && <PlatformIcon platform={p} className="h-3 w-3" />}
                {p === "all" ? "All posts" : p === "x" ? "X" : p}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((post, i) => (
            <a
              key={i}
              href={post.url}
              target="_blank"
              rel="noreferrer"
              data-testid={SOCIAL_FEED.card(i)}
              className="card group relative overflow-hidden block"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[var(--tt-navy)]">
                <img
                  src={post.image}
                  alt=""
                  className="w-full h-full object-cover transition duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/0" />
                <div
                  className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white"
                  style={{ background: platformColor(post.platform) }}
                >
                  <PlatformIcon platform={post.platform} className="h-3 w-3" />
                  <span className="capitalize">{post.platform === "x" ? "X" : post.platform}</span>
                </div>
                {post.platform === "youtube" && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-14 w-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl">
                      <Play className="h-5 w-5 text-[var(--tt-navy)] ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                )}
                <div className="absolute bottom-3 right-3 text-[11px] text-white/90 bg-black/30 backdrop-blur px-2 py-1 rounded-full">
                  {post.posted}
                </div>
              </div>
              <div className="p-5">
                <p className="text-sm text-[var(--tt-ink)] leading-relaxed line-clamp-3">
                  {post.caption}
                </p>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-3 text-[var(--tt-muted)]">
                    <span className="inline-flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> 124</span>
                    <span className="inline-flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> 18</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[var(--tt-navy)] font-semibold group-hover:text-[var(--tt-accent)] transition">
                    View post <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 text-sm text-[var(--tt-muted)]">
          <span>Catch us across the web —</span>
          {["facebook", "instagram", "whatsapp", "linkedin", "x", "youtube"].map((p) => (
            <a key={p} href="#" aria-label={p} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white border border-[var(--tt-line)] hover:scale-105 transition" style={{ color: platformColor(p) }}>
              <PlatformIcon platform={p} className="h-4 w-4" />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SocialFeed;
